import { SystemError } from '@repo/errors';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { nowIso } from '@/lib/utils';

import {
  createTemplate,
  deleteTemplate,
  duplicateTemplate,
  getTemplate,
  listTemplatesAsSaved,
  publishTemplate,
  restoreTemplate,
  type TemplateDefinition,
  type TemplateResponse,
  unpublishTemplate,
  updateTemplate,
} from '@/api/template-api';
import { mapWizardStateToContract } from '@/domain/templates/template-mapper';
import {
  DEFAULT_TEMPLATE_STATUSES,
  type SavedTemplate,
  type TemplateStatus,
} from '@/types/saved-template';
import type { WizardState } from '@/types/template';

const TEMPLATES_KEY = ['templates'] as const;

const TEMPLATE_LISTS_KEY = [...TEMPLATES_KEY, 'list'] as const;

/**
 * Query key for one status-filtered template list. Statuses are sorted so the
 * same set in any order shares a cache entry.
 */
export function templateListKeyFor(statuses: readonly TemplateStatus[]) {
  return [...TEMPLATE_LISTS_KEY, { statuses: [...statuses].sort() }] as const;
}

/** Query key for a single template by id. */
function templateKey(id: string) {
  return [...TEMPLATES_KEY, id] as const;
}

/**
 * Lists saved templates in the given lifecycle statuses (filtered server-side).
 * The statuses are part of the query key, so changing the filter is a fresh
 * query rather than a cache invalidation.
 */
export function useTemplates(statuses: readonly TemplateStatus[] = DEFAULT_TEMPLATE_STATUSES) {
  // An empty list means the default server-side, so key it as the default.
  const requested = statuses.length > 0 ? statuses : DEFAULT_TEMPLATE_STATUSES;
  return useQuery({
    queryKey: templateListKeyFor(requested),
    queryFn: () => listTemplatesAsSaved(requested),
  });
}

/**
 * Fetches a single template by id. Pass `null` to disable the query
 * (typed so React Query's `enabled` flag short-circuits the
 * unauthenticated path).
 */
export function useTemplate(id: string | null) {
  return useQuery({
    queryKey: id ? templateKey(id) : ['templates', 'disabled'],
    queryFn: () => {
      if (!id) throw new SystemError('useTemplate called without an id');
      return getTemplate(id);
    },
    enabled: id !== null,
  });
}

/**
 * Updates a template by id. Invalidates the templates list on success
 * so consumers refetch.
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, definition }: { id: string; definition: TemplateDefinition }) =>
      updateTemplate(id, definition),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

/**
 * Soft-deletes a template (A3 / D33). **Optimistic** — the row disappears
 * from the cached list immediately so the user sees instant feedback;
 * if the API call fails the cache is restored from a pre-mutation
 * snapshot and the user sees the row reappear.
 *
 * `onSettled` invalidates the list on both success and failure so the
 * server's canonical state wins regardless.
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: TEMPLATE_LISTS_KEY });
      const snapshot = queryClient.getQueriesData<SavedTemplate[]>({
        queryKey: TEMPLATE_LISTS_KEY,
      });
      for (const [key, current] of snapshot) {
        if (!current) continue;
        const next = listIncludesStatus({ key, status: 'deleted' })
          ? current.map((template) =>
              template.id === id ? { ...template, status: 'deleted' as const } : template,
            )
          : current.filter((template) => template.id !== id);
        queryClient.setQueryData(key, next);
      }
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      restoreSnapshot(queryClient, context?.snapshot);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

/**
 * Duplicates an existing template with an optional new name (A3 / D33).
 * **Optimistic** — inserts a placeholder draft row at the top of the
 * cached list using a temporary ID, then reconciles to the real ID on
 * success or rolls back on error. `onSettled` invalidates the list so
 * the server's canonical row replaces the placeholder shape.
 */
export function useDuplicateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newName }: { id: string; newName?: string }) =>
      duplicateTemplate(id, newName),
    onMutate: async ({ id, newName }) => {
      await queryClient.cancelQueries({ queryKey: TEMPLATE_LISTS_KEY });
      const snapshot = queryClient.getQueriesData<SavedTemplate[]>({
        queryKey: TEMPLATE_LISTS_KEY,
      });
      for (const [key, current] of snapshot) {
        if (!current || !listIncludesStatus({ key, status: 'draft' })) continue;
        const source = current.find((template) => template.id === id);
        if (!source) continue;
        const placeholder: SavedTemplate = {
          ...source,
          id: `optimistic-${Date.now()}`,
          name: newName ?? `${source.name} (copy)`,
          status: 'draft',
          createdAt: nowIso(),
          modifiedAt: nowIso(),
        };
        queryClient.setQueryData(key, [placeholder, ...current]);
      }
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      restoreSnapshot(queryClient, context?.snapshot);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

/**
 * Restores a soft-deleted template to the draft or published state it had
 * before deletion. A single server action; invalidates the templates list.
 */
export function useRestoreTemplate() {
  return useLifecycleAction(restoreTemplate);
}

/**
 * Promotes a draft template to published. A single server action (no
 * fetch-then-patch); invalidates the templates list.
 */
export function usePublishTemplate() {
  return useLifecycleAction(publishTemplate);
}

/**
 * Demotes a published template back to draft. A single server action (no
 * fetch-then-patch); invalidates the templates list.
 */
export function useUnpublishTemplate() {
  return useLifecycleAction(unpublishTemplate);
}

/**
 * Builds a mutation that calls a lifecycle action endpoint with the
 * template ID and invalidates the templates list on success.
 */
function useLifecycleAction(action: (id: string) => Promise<TemplateResponse>) {
  const queryClient = useQueryClient();
  return useMutation<TemplateResponse, Error, string>({
    mutationFn: (id) => action(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

/**
 * True when a cached template-list query (keyed by {@link templateListKeyFor})
 * includes `status`, so optimistic updates can mirror the server's filtering.
 */
function listIncludesStatus({ key, status }: { key: QueryKey; status: TemplateStatus }): boolean {
  const params = key[2];
  return (
    typeof params === 'object' &&
    params !== null &&
    'statuses' in params &&
    Array.isArray(params.statuses) &&
    params.statuses.includes(status)
  );
}

/**
 * Restores cached list data from a snapshot captured in `onMutate`.
 * Used by the optimistic mutations to roll back when the underlying
 * API call fails.
 */
function restoreSnapshot(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ReadonlyArray<readonly [QueryKey, SavedTemplate[] | undefined]> | undefined,
): void {
  if (!snapshot) return;
  for (const [key, value] of snapshot) {
    queryClient.setQueryData(key, value);
  }
}

/**
 * Wizard's create-or-update save path. Branches on `existingId` so the
 * "Save" button on an edit-mode wizard reuses the same mutation as the
 * "Create" button on the new-template wizard. Consolidates the mapper
 * call so consumers stay decoupled from the contract shape.
 */
export function useSaveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ state, existingId }: SaveTemplateVars) => {
      const contract = mapWizardStateToContract(state);
      return existingId ? updateTemplate(existingId, contract) : createTemplate(contract);
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      if (vars.existingId) {
        void queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY, vars.existingId] });
      }
    },
  });
}

/** Variables for {@link useSaveTemplate}: the state to persist, and the id when updating. */
interface SaveTemplateVars {
  state: WizardState;
  existingId?: string;
}
